import React, { createContext, useContext, useState, useEffect } from 'react';
import deviceService from '../services/deviceService';

const DeviceContext = createContext({});

export const useDevices = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevices must be used within a DeviceProvider');
  }
  return context;
};

export const DeviceProvider = ({ children }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    locationId: null,
    search: ''
  });

  const loadDevices = async (locationId = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = locationId
        ? await deviceService.getDevicesByLocation(locationId)
        : await deviceService.getAllDevices();
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading devices:', err);
      setError('Failed to load devices');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const createDevice = async (deviceData) => {
    setError(null);
    try {
      const newDevice = await deviceService.createDevice(deviceData);
      await loadDevices(filters.locationId);
      return newDevice;
    } catch (err) {
      console.error('Error creating device:', err);
      setError('Failed to create device');
      throw err;
    }
  };

  const updateDevice = async (deviceId, updates) => {
    setError(null);
    try {
      const updatedDevice = await deviceService.updateDevice(deviceId, updates);
      await loadDevices(filters.locationId);
      return updatedDevice;
    } catch (err) {
      console.error('Error updating device:', err);
      setError('Failed to update device');
      throw err;
    }
  };

  const deleteDevice = async (deviceId) => {
    setError(null);
    try {
      await deviceService.deleteDevice(deviceId);
      await loadDevices(filters.locationId);
    } catch (err) {
      console.error('Error deleting device:', err);
      setError('Failed to delete device');
      throw err;
    }
  };

  const getDeviceById = async (deviceId) => {
    setLoading(true);
    setError(null);
    try {
      const device = await deviceService.getDeviceById(deviceId);
      setSelectedDevice(device);
      return device;
    } catch (err) {
      console.error('Error fetching device:', err);
      setError('Failed to load device details');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateDeviceStatus = async (deviceId, status) => {
    setError(null);
    try {
      const updatedDevice = await deviceService.updateDeviceStatus(deviceId, status);
      await loadDevices(filters.locationId);
      return updatedDevice;
    } catch (err) {
      console.error('Error updating device status:', err);
      setError('Failed to update device status');
      throw err;
    }
  };

  const moveDevice = async (deviceId, newLocationId) => {
    setError(null);
    try {
      const updatedDevice = await deviceService.moveDevice(deviceId, newLocationId);
      await loadDevices(filters.locationId);
      return updatedDevice;
    } catch (err) {
      console.error('Error moving device:', err);
      setError('Failed to move device');
      throw err;
    }
  };

  const getDeviceHistory = async (deviceId) => {
    try {
      return await deviceService.getDeviceHistory(deviceId);
    } catch (err) {
      console.error('Error fetching device history:', err);
      setError('Failed to load device history');
      throw err;
    }
  };

  const getDeviceMetrics = async (deviceId, period = '24h') => {
    try {
      return await deviceService.getDeviceMetrics(deviceId, period);
    } catch (err) {
      console.error('Error fetching device metrics:', err);
      setError('Failed to load device metrics');
      throw err;
    }
  };

  const applyFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const getFilteredDevices = () => {
    let filtered = [...devices];

    if (filters.status !== 'all') {
      filtered = filtered.filter(device => device.status === filters.status);
    }

    if (filters.type !== 'all') {
      filtered = filtered.filter(device => device.type === filters.type);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(device =>
        device.name?.toLowerCase().includes(searchLower) ||
        device.serialNumber?.toLowerCase().includes(searchLower) ||
        device.model?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const getDeviceStats = () => {
    const stats = {
      total: devices.length,
      online: 0,
      offline: 0,
      maintenance: 0,
      byType: {},
      byLocation: {}
    };

    devices.forEach(device => {
      if (device.status === 'online') stats.online++;
      else if (device.status === 'offline') stats.offline++;
      else if (device.status === 'maintenance') stats.maintenance++;

      if (device.type) {
        stats.byType[device.type] = (stats.byType[device.type] || 0) + 1;
      }

      if (device.locationId) {
        stats.byLocation[device.locationId] = (stats.byLocation[device.locationId] || 0) + 1;
      }
    });

    return stats;
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const value = {
    devices,
    selectedDevice,
    loading,
    error,
    filters,
    fetchDevices: loadDevices,  // Alias for compatibility
    loadDevices,
    createDevice,
    updateDevice,
    deleteDevice,
    getDeviceById,
    updateDeviceStatus,
    moveDevice,
    getDeviceHistory,
    getDeviceMetrics,
    setSelectedDevice,
    applyFilters,
    getFilteredDevices,
    getDeviceStats,
    canManageDevices: true  // This should be handled by permissions
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};

export default DeviceContext;